import React, { useRef, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  CategoryScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import {countGuestStatuses} from '../Analitics_functions/Analitics';

ChartJS.register(CategoryScale, BarElement, Title, Tooltip, ChartDataLabels);

const barChartColors = {
  total: '#f2f2f2',
  served: '#beddd0',
  canceled: '#f0d7d2',
  waiting: '#dfceab',
};

const BarChart = ({ data, stats, selectedQueue, dateRange }) => {

  if (!Object.entries(data)) return;
  const { startDate, endDate } = dateRange;

  const queueDataList = Object.keys(data).filter((queueId) =>
    selectedQueue.includes(queueId)
  );

  if (!queueDataList.length) {
    return <p>No data available for the selected queues.</p>;
  }
  

  const dates = [];
  const currentDate = new Date(startDate);
  const endDateObj = new Date(endDate);
  while (currentDate <= endDateObj) {
    dates.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const labels = [];
  const datasets = [];

  dates.forEach((date) => {
    queueDataList.forEach((queueId) => {
      if (!labels.includes(date)) {
        labels.push(`${date}`);
      }
    });
  });

  queueDataList.forEach((queueId, index) => {
    let servedData = [];
    const canceledData = [];
    const waitingData = [];
    const totalGuests = [];

    dates.forEach((date) => {
      const dayData = countGuestStatuses(data[queueId]?.daily_stats, date);

      const defaultValue = 0;

      servedData.push(dayData.guestsServed || defaultValue);
      canceledData.push(dayData.canceledGuests || defaultValue);
      waitingData.push(dayData.waitingGuests || defaultValue);
      totalGuests.push(
        dayData.guestsServed + dayData.canceledGuests + dayData.waitingGuests);
    });

    datasets.push(
      {
        label: `${data[queueId].name}`,
        data: 0,
        backgroundColor: `rgba(255, 255, 255, 0)`,
        categoryPercentage: 0.8,
        barPercentage: 0.5,
      },
      {
        label: `${data[queueId].name}`,
        data: 0,
        backgroundColor: `rgba(255, 255, 255, 0)`,
        categoryPercentage: 0.8,
        barPercentage: 0.5,
      },
      {
        label: `${data[queueId].name}`,
        data: totalGuests,
        backgroundColor: barChartColors.total,
        borderWidth: 1,
        categoryPercentage: 0.8,
        barPercentage: 1,
      },
      {
        label: `Served - ${data[queueId].name}`,
        data: servedData,
        backgroundColor: barChartColors.served,
        borderWidth: 1,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      },
      {
        label: `Canceled - ${data[queueId].name}`,
        data: canceledData,
        backgroundColor: barChartColors.canceled,
        borderWidth: 1,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      },
      {
        label: `Waiting - ${data[queueId].name}`,
        data: waitingData,
        backgroundColor: barChartColors.waiting,
        borderWidth: 1,
        categoryPercentage: 0.8,
        barPercentage: 0.6,
      }
    );
  });

  const chartData = {
    labels: labels,
    datasets: datasets,
  };

  const options = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            if (tooltipItem.raw < 1) {
              return '';
            }
            return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
          },
        },
      },
      legend: {
        display: true,
        labels: {
          generateLabels: (chart) => {
            return [
              { text: 'Total', fillStyle: barChartColors.total },
              { text: 'Served', fillStyle: barChartColors.served },
              { text: 'Removed', fillStyle: barChartColors.canceled },
              { text: 'Waiting', fillStyle: barChartColors.waiting },
            ];
          },
        },
        onClick: () => {},
      },
      customLegend: false,
      title: {
        display: true,
        text: `Queue Status Overview: ${queueDataList
          .map((id) => data[id].name)
          .join(', ')}`,
      },
      datalabels: {
        color: '#000',
        align: 'center',
        anchor: 'center',
        font: {
          weight: 'bold',
        },
        formatter: (value, context) => {
          const queueId =
            queueDataList[context.dataIndex % queueDataList.length];
          if (
            value &&
            context.dataset.backgroundColor.includes(barChartColors.total)
          ) {
            const label = context.dataset.label;
            if (label.length > 6) {
              return `${label.slice(0, 6)}...`;
            }
            return label;
          } else {
            return '';
          }
        },
      },
    },
    scales: {
      x: {
        stacked: false,
        title: { display: true, text: 'Date' },
        ticks: { autoSkip: false, maxRotation: 90, minRotation: 45 },
      },
      y: {
        stacked: false,
        beginAtZero: true,
        title: { display: true, text: 'Number of Guests' },
        ticks: {
          stepSize: 1,
          precision: 0,
        },
        afterDataLimits: (scale) => {
          scale.max = scale.max < 5 ? scale.max + 1 : scale.max + 5;
        },
      },
    },
  };

  return (
    <>
      <Bar data={chartData} options={options} />
    </>
  );
};

export default BarChart;
